import React from "react";
import { intlShape } from "@ai360/core/intl";
import { messages } from "../i18n-messages";
import { TextInput, Section, SubSection, Checkbox } from "~/core";
import "./component.css";
import { useIntl } from "react-intl";

const API_URL = "apiUrl";

interface IAgvanceIntegrationAliasList {
    apiUrl?: string;
    databaseId?: string;
    isAgvanceCustomerExists?: string;
    linkedCropOnly?: string;
    linkedProductOnly?: string;
    removeProperCasing?: string;
    requireCustomerSearchFlag?: string;
    userId?: string;
    userPassword?: string;
}

interface IAgvanceIntegrationProps {
    aliasList: IAgvanceIntegrationAliasList;
    // Optional: formatMessage comes from useIntl(), and no caller passes intl.
    intl?: intlShape;
    isAgvancePersonality?: boolean;
    isEditMode?: boolean;
    onTextChange: (formKey: string, value: string | boolean, callback?: () => void) => void;
    record: Record<string, any>;
}

const AgvanceIntegration = (props: IAgvanceIntegrationProps): React.ReactElement => {
    const { formatMessage } = useIntl();
    const { aliasList, isAgvancePersonality, isEditMode, onTextChange, record } = props;
    return (
        <Section
            headerText={formatMessage(messages.agvanceIntegration)}
            className="agvance-integration"
            testId="agvance-integration-section"
        >
            {!(API_URL in aliasList) ? null : (
                <TextInput
                    placeholderText={formatMessage(messages.apiUrl)}
                    maxLength={400}
                    onChange={(value) => onTextChange(aliasList[API_URL], value)}
                    value={record[aliasList[API_URL]]}
                    containerClassNames={["text-input-stretch"]}
                    testId="agvance-integration-api-url-input"
                />
            )}
            <SubSection testId="agvance-integration-database-id-subsection">
                <TextInput
                    placeholderText={formatMessage(messages.databaseId)}
                    maxLength={50}
                    onChange={(value) => onTextChange(aliasList.databaseId, value)}
                    value={record[aliasList.databaseId]}
                    disabled={isEditMode && record[aliasList.isAgvanceCustomerExists]}
                    testId="agvance-integration-database-id-input"
                />
                {!isAgvancePersonality ? (
                    <Checkbox
                        label={formatMessage(messages.requireCustomerSearch)}
                        onChange={(e, value) =>
                            onTextChange(aliasList.requireCustomerSearchFlag, value)
                        }
                        value={record[aliasList.requireCustomerSearchFlag]}
                        testId="agvance-integration-require-customer-search-checkbox"
                    />
                ) : null}
                {!isAgvancePersonality ? null : (
                    <TextInput
                        placeholderText={formatMessage(messages.userId)}
                        maxLength={50}
                        onChange={(value) => onTextChange(aliasList.userId, value)}
                        value={record[aliasList.userId]}
                        testId="agvance-integration-user-id-input"
                    />
                )}
            </SubSection>
            {!isAgvancePersonality ? null : (
                <SubSection
                    className="agvance-integration-checkbox-cont"
                    testId="agvance-integration-user-password-subsection"
                >
                    <TextInput
                        placeholderText={formatMessage(messages.userPassword)}
                        maxLength={100}
                        password
                        onChange={(value) => onTextChange(aliasList.userPassword, value)}
                        value={record[aliasList.userPassword]}
                        containerClassNames={["text-input-stretch"]}
                        testId="agvance-integration-user-password-input"
                    />
                    {!aliasList.requireCustomerSearchFlag ? null : (
                        <Checkbox
                            label={formatMessage(messages.requireCustomerSearch)}
                            onChange={(e, value) =>
                                onTextChange(aliasList.requireCustomerSearchFlag, value)
                            }
                            value={record[aliasList.requireCustomerSearchFlag]}
                            testId="agvance-integration-edit-require-customer-search-checkbox"
                        />
                    )}
                </SubSection>
            )}
            {!isAgvancePersonality ||
            !(
                aliasList.removeProperCasing ||
                aliasList.linkedProductOnly ||
                aliasList.linkedCropOnly
            ) ? null : (
                <SubSection
                    className="agvance-integration-checkbox-cont agvance-integration-checkbox-grid"
                    testId="agvance-integration-options-subsection"
                >
                    {!aliasList.removeProperCasing ? null : (
                        <Checkbox
                            label={formatMessage(messages.removeProperCasing)}
                            onChange={(e, value) =>
                                onTextChange(aliasList.removeProperCasing, value)
                            }
                            value={record[aliasList.removeProperCasing]}
                            testId="agvance-integration-remove-proper-casing-checkbox"
                        />
                    )}
                    {!aliasList.linkedProductOnly ? null : (
                        <Checkbox
                            label={formatMessage(messages.linkedProductOnly)}
                            onChange={(e, value) =>
                                onTextChange(aliasList.linkedProductOnly, value)
                            }
                            value={record[aliasList.linkedProductOnly]}
                            testId="agvance-integration-linked-product-only-checkbox"
                        />
                    )}
                    {!aliasList.linkedCropOnly ? null : (
                        <Checkbox
                            label={formatMessage(messages.linkedCropOnly)}
                            onChange={(e, value) => onTextChange(aliasList.linkedCropOnly, value)}
                            value={record[aliasList.linkedCropOnly]}
                            testId="agvance-integration-linked-crop-only-checkbox"
                        />
                    )}
                </SubSection>
            )}
        </Section>
    );
};

export default AgvanceIntegration;
